import type { PortableTextBlock } from 'sanity';
import { stegaClean } from '@sanity/client/stega';
import type { IPricingV2Discount } from '@/sanity/templates';
import {
  applyAffiliateDiscountToRichtext,
  applyAffiliateDiscountToText,
} from '@/lib/affiliateDiscount';
import { resolveOfferLocale, rewriteCouponText } from '@/lib/affiliateNewOffer';

interface OfferCopyFields {
  pricingV2Discount?: IPricingV2Discount;
  pricingSectionV3?: { discountPercent?: number };
  heroChipText?: PortableTextBlock[];
  vipDealItems?: Array<{ value: string; unit: string; label: string }>;
  stepsItems?: Array<{
    captionText?: string;
    coupon?: { priceOriginal?: string; priceCurrent?: string };
  }>;
  faqItems?: Array<{ answer: PortableTextBlock[] }>;
  finalCtaSubheading?: string;
}

const OFFER_PERCENT_RE = /\d+(?:[.,]\d+)?(?=\s*%\s*(?:off|rabatt|discount)\b)/gi;

export function rewriteOfferPercent<T extends string | undefined | null>(
  text: T,
  percent: number | undefined,
): T {
  if (!text || percent == null) return text;
  return text.replace(OFFER_PERCENT_RE, String(percent)) as T;
}

function lowestPercent(values: Array<number | undefined>): number | undefined {
  const set = values.filter((value): value is number => typeof value === 'number' && value > 0);
  return set.length ? Math.min(...set) : undefined;
}

// Rounded to whole dollars, like the price cards of the pricing section.
function discountedPrice(original: string | undefined, percent: number | undefined): string | undefined {
  const value = Number(stegaClean(original)?.replace(/[^\d.]/g, ''));
  if (!value || percent == null) return undefined;
  return `$${Math.round(value * (1 - percent / 100))}`;
}

// Every % in the copy takes the number the plan cards are priced with; only the number is swapped.
export function withAffiliateOfferCopy<T extends OfferCopyFields>(
  data: T,
  assetKey: string | undefined,
): T {
  const discount = data?.pricingV2Discount;
  const futures = assetKey !== 'cfd_crypto';
  const crypto = assetKey !== 'futures';
  const percent =
    lowestPercent([data?.pricingSectionV3?.discountPercent]) ??
    lowestPercent([
      ...(futures ? [discount?.futuresCoreDiscountPercent, discount?.futuresInstantFundingDiscountPercent] : []),
      ...(crypto ? [discount?.cfdCryptoCoreDiscountPercent, discount?.cfdCryptoInstantFundingDiscountPercent] : []),
    ]);
  if (percent == null) return data;

  const overrides = { isActive: true, challenge: percent, instantFunding: percent };
  const locale =
    resolveOfferLocale([
      data.finalCtaSubheading ?? '',
      ...(data.stepsItems ?? []).map((item) => item.captionText ?? ''),
    ]) ?? 'en';

  return {
    ...data,
    heroChipText: applyAffiliateDiscountToRichtext(data.heroChipText, overrides),
    vipDealItems: data.vipDealItems?.map((item) =>
      stegaClean(item.unit)?.includes('%') ? { ...item, value: String(percent) } : item,
    ),
    stepsItems: data.stepsItems?.map((item) => ({
      ...item,
      captionText: rewriteCouponText(item.captionText, percent, locale),
      coupon: item.coupon && {
        ...item.coupon,
        priceCurrent: discountedPrice(item.coupon.priceOriginal, percent) ?? item.coupon.priceCurrent,
      },
    })),
    faqItems: data.faqItems?.map((item) => ({
      ...item,
      answer: applyAffiliateDiscountToRichtext(item.answer, overrides),
    })),
    finalCtaSubheading: applyAffiliateDiscountToText(data.finalCtaSubheading, overrides),
  } as T;
}
